import { z } from 'zod';

import {
  SEW_CUSTOMER_PARTIES_MODE,
  SEW_CUSTOMER_PARTIES_MODE_FIELD,
  SEW_CUSTOMER_PARTIES_MODE_VALUES,
  SEW_METADATA_KEYS,
  SEW_SEPARATE_PARTY_KEYS,
} from '@/reseller/sew/metadata/sew-metadata.constants';
import { TSewCustomerPartiesValues } from '@/reseller/sew/metadata/sew-metadata.types';
import { administratorOnboardingSchema } from '@/shared/features/onboarding/components/administrator/administrator.validation';
import { companyOnboardingSchema } from '@/shared/features/onboarding/components/company/company.validation';
import {
  DEFAULT_ERROR_KEY,
  optionalString,
  requiredString,
} from '@/shared/utils/validation';

// Demo data and MFA enforcement are both decided on the final review step.
export const onboardOverviewInputSchema = z.object({
  importDemoData: z.boolean().optional(),
  enforceMfa: z.boolean().optional(),
});

export const sewTenantMetaDataInputSchema = (required?: boolean) =>
  z.object({
    [SEW_METADATA_KEYS.billingRegion]: required
      ? requiredString()
      : optionalString(),
    [SEW_METADATA_KEYS.customer]: required
      ? requiredString()
      : optionalString(),
    [SEW_METADATA_KEYS.sewCustomerId]: required
      ? requiredString()
      : optionalString(),
    [SEW_METADATA_KEYS.billingType]: required
      ? requiredString()
      : optionalString(),
    // Required only when the parties differ — see validateSewCustomerParties.
    [SEW_METADATA_KEYS.shippingCustomer]: optionalString(),
    [SEW_METADATA_KEYS.shippingCustomerId]: optionalString(),
    [SEW_METADATA_KEYS.billingCustomer]: optionalString(),
    [SEW_METADATA_KEYS.billingCustomerId]: optionalString(),
  });

export const sewCustomerPartiesModeSchema = z
  .enum(SEW_CUSTOMER_PARTIES_MODE_VALUES)
  .optional();

export const validateSewCustomerParties = (
  values: TSewCustomerPartiesValues,
  ctx: z.RefinementCtx,
) => {
  const mode = values[SEW_CUSTOMER_PARTIES_MODE_FIELD];

  if (mode !== SEW_CUSTOMER_PARTIES_MODE.separate) return;

  SEW_SEPARATE_PARTY_KEYS.forEach((key) => {
    if (values.metaData?.[key]?.trim()) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: DEFAULT_ERROR_KEY,
      path: ['metaData', key],
    });
  });
};

export const onboardCompanyStepSchema = companyOnboardingSchema.extend({
  groupId: optionalString(),
});

// SEW collects the EURODRIVE billing metadata on its own step; other resellers
// never see it.
export const sewBillingStepSchema = z
  .object({
    [SEW_CUSTOMER_PARTIES_MODE_FIELD]: sewCustomerPartiesModeSchema,
    metaData: sewTenantMetaDataInputSchema(true),
  })
  .superRefine(validateSewCustomerParties);

export const onboardTenantSchema = companyOnboardingSchema
  .merge(administratorOnboardingSchema)
  .merge(onboardOverviewInputSchema)
  .extend({
    enforceMfa: z.boolean().optional(),
    metaData: sewTenantMetaDataInputSchema().optional(),
    groupId: optionalString(),
    [SEW_CUSTOMER_PARTIES_MODE_FIELD]: sewCustomerPartiesModeSchema,
  });
